import { FormEvent } from "react"
import { useForm, usePage } from "@inertiajs/react"
import { BadgeCheck, ExternalLink } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import type { PageProps } from "@/types/inertia"

export type VerificationFormProps = {
  verifiable_type: "Destination" | "Place" | "Trail" | "TransportRoute"
  verifiable_id: number
  aspects: string[]
  confidences: string[]
  sources: string[]
  default_source: string
  default_verified_at: string
  recent_verifications: Array<{
    id: number
    aspect: string
    source_name: string
    source_url: string | null
    confidence: string
    verified_at: string
    editor_email: string
    internal_note: string | null
  }>
}

type VerificationFormData = {
  verifiable_type: string
  verifiable_id: string
  aspect: string
  source_name: string
  source_url: string
  confidence: string
  verified_at: string
  internal_note: string
}

export function VerificationQuickAdd({ verificationForm }: { verificationForm: VerificationFormProps }) {
  const { props } = usePage<PageProps>()
  const form = useForm<VerificationFormData>({
    verifiable_type: verificationForm.verifiable_type,
    verifiable_id: verificationForm.verifiable_id.toString(),
    aspect: verificationForm.aspects[0] ?? "general",
    source_name: verificationForm.default_source,
    source_url: "",
    confidence: "high",
    verified_at: verificationForm.default_verified_at,
    internal_note: "",
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    form.post("/admin/verifications", { preserveScroll: true })
  }

  return (
    <section className="mt-8 border-t border-hairline pt-7">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent-faded text-accent-display">
          <BadgeCheck className="h-5 w-5" aria-hidden />
        </span>
        <div>
          <h2>Record verification</h2>
          <p className="mt-1">A phone-friendly quick add for checks completed in the field.</p>
        </div>
      </div>

      <form onSubmit={submit} className="mt-5 rounded-xl border border-hairline bg-surface p-4 shadow-sm sm:p-5">
        <input type="hidden" name="verifiable_type" value={form.data.verifiable_type} />
        <input type="hidden" name="verifiable_id" value={form.data.verifiable_id} />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Aspect" error={props.errors?.aspect}>
            <Select value={form.data.aspect} onChange={(event) => form.setData("aspect", event.target.value)}>
              {verificationForm.aspects.map((aspect) => <option key={aspect} value={aspect}>{titleize(aspect)}</option>)}
            </Select>
          </Field>
          <Field label="Confidence" error={props.errors?.confidence}>
            <Select value={form.data.confidence} onChange={(event) => form.setData("confidence", event.target.value)}>
              {verificationForm.confidences.map((confidence) => (
                <option key={confidence} value={confidence}>{titleize(confidence)}</option>
              ))}
            </Select>
          </Field>
          <Field label="Checked on" error={props.errors?.verified_at}>
            <Input type="date" value={form.data.verified_at} onChange={(event) => form.setData("verified_at", event.target.value)} />
          </Field>
          <Field label="Source" error={props.errors?.source_name}>
            <Select value={form.data.source_name} onChange={(event) => form.setData("source_name", event.target.value)}>
              {verificationForm.sources.map((source) => <option key={source} value={source}>{source}</option>)}
            </Select>
          </Field>
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Source URL (optional)" error={props.errors?.source_url}>
            <Input
              type="url"
              placeholder="https://"
              value={form.data.source_url}
              onChange={(event) => form.setData("source_url", event.target.value)}
            />
          </Field>
          <Field label="Internal note (optional)" error={props.errors?.internal_note}>
            <Input value={form.data.internal_note} onChange={(event) => form.setData("internal_note", event.target.value)} />
          </Field>
        </div>
        <Button type="submit" className="mt-5 w-full sm:w-auto" disabled={form.processing}>
          {form.processing ? "Saving..." : "Record verification"}
        </Button>
      </form>

      {verificationForm.recent_verifications.length > 0 && (
        <div className="mt-6">
          <h3>Recent checks</h3>
          <div className="mt-3 divide-y divide-hairline rounded-xl border border-hairline bg-surface">
            {verificationForm.recent_verifications.map((verification) => (
              <div key={verification.id} className="grid gap-1 px-4 py-3 text-sm sm:grid-cols-[9rem_1fr_auto] sm:items-center sm:gap-4">
                <strong>{titleize(verification.aspect)}</strong>
                <span className="text-ink-muted">
                  {verification.source_url ? (
                    <a href={verification.source_url} className="inline-flex items-center gap-1" target="_blank" rel="noreferrer">
                      {verification.source_name}<ExternalLink className="h-3 w-3" aria-hidden />
                    </a>
                  ) : verification.source_name}
                  {` · ${verification.editor_email}`}
                </span>
                <span className="text-ink-muted">{verification.verified_at} · {titleize(verification.confidence)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  )
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label>{label}</label>
      <div className="mt-1.5">{children}</div>
      {error && <p className="mt-1 text-sm text-danger">{error}</p>}
    </div>
  )
}

function titleize(value: string) {
  return value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
}
